import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Timer, X, Play, Pause, RotateCcw, Volume2 } from 'lucide-react';

const ExerciseTimer = ({ exerciseName, initialDuration = 30, onClose, onComplete }) => {
  const [duration, setDuration] = useState(initialDuration);
  const [timeLeft, setTimeLeft] = useState(initialDuration);
  const [isRunning, setIsRunning] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const audioRef = useRef(null);

  // Create audio context for chime
  useEffect(() => {
    // Create audio element for chime sound
    const audio = new Audio();
    // Use a data URI for a simple chime sound (440Hz beep)
    audioRef.current = audio;
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Play chime function using Web Audio API
  const playChime = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create multiple beeps for a more noticeable chime
      const playBeep = (startTime, frequency, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.5, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      const now = audioContext.currentTime;
      // Play a pleasant chime sequence (C-E-G chord)
      playBeep(now, 523.25, 0.3);        // C5
      playBeep(now + 0.15, 659.25, 0.3); // E5
      playBeep(now + 0.30, 783.99, 0.5); // G5
      
      // Second set of chimes
      setTimeout(() => {
        const now2 = audioContext.currentTime;
        playBeep(now2, 783.99, 0.3);     // G5
        playBeep(now2 + 0.15, 987.77, 0.5); // B5
      }, 600);
      
    } catch (error) {
      console.log('Audio not supported:', error);
      // Fallback: try to vibrate if available
      if (navigator.vibrate) {
        navigator.vibrate([200, 100, 200, 100, 400]);
      }
    }
  };

  useEffect(() => {
    let interval;
    if (isRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            // Play chime when timer ends
            playChime();
            onComplete?.();
            return 0;
          }
          // Play warning beep at 3 seconds
          if (prev === 4) {
            try {
              const audioContext = new (window.AudioContext || window.webkitAudioContext)();
              const oscillator = audioContext.createOscillator();
              const gainNode = audioContext.createGain();
              oscillator.connect(gainNode);
              gainNode.connect(audioContext.destination);
              oscillator.frequency.value = 880;
              oscillator.type = 'sine';
              gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
              gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
              oscillator.start();
              oscillator.stop(audioContext.currentTime + 0.15);
            } catch (e) {
              // Silent fail
            }
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, timeLeft, onComplete]);

  const startTimer = () => {
    setHasStarted(true);
    setIsRunning(true);
  };

  const pauseTimer = () => {
    setIsRunning(false);
  };

  const resumeTimer = () => {
    if (timeLeft > 0) {
      setIsRunning(true);
    }
  };

  const resetTimer = () => {
    setTimeLeft(duration);
    setIsRunning(false);
    setHasStarted(false);
  };

  const adjustDuration = (seconds) => {
    const newDuration = Math.max(5, duration + seconds);
    setDuration(newDuration);
    if (!hasStarted) {
      setTimeLeft(newDuration);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = () => {
    return ((duration - timeLeft) / duration) * 100;
  };

  // Test chime button
  const testChime = () => {
    playChime();
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="exercise-timer-overlay">
      <Card className="glass border-blood-700 p-6 max-w-md w-full relative">
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="absolute top-4 right-4 text-stone-400 hover:text-white"
          data-testid="close-exercise-timer-btn"
        >
          <X className="w-5 h-5" />
        </Button>

        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-blood-600 to-blood-800 mb-2">
            <Timer className="w-7 h-7 text-white" />
          </div>

          <div>
            <h2 className="text-xl font-bold text-blood-400" data-testid="exercise-timer-title">
              EXERCISE TIMER
            </h2>
            {exerciseName && (
              <p className="text-stone-300 text-sm mt-1">{exerciseName}</p>
            )}
          </div>

          {/* Duration Adjustment (only before starting) */}
          {!hasStarted && (
            <div className="flex items-center justify-center gap-4 py-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => adjustDuration(-10)}
                className="glass border-stone-600 text-stone-300 hover:bg-stone-800"
              >
                -10s
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => adjustDuration(-5)}
                className="glass border-stone-600 text-stone-300 hover:bg-stone-800"
              >
                -5s
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => adjustDuration(5)}
                className="glass border-stone-600 text-stone-300 hover:bg-stone-800"
              >
                +5s
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => adjustDuration(10)}
                className="glass border-stone-600 text-stone-300 hover:bg-stone-800"
              >
                +10s
              </Button>
            </div>
          )}

          {/* Circular Progress */}
          <div className="relative w-48 h-48 mx-auto">
            <svg className="w-48 h-48 transform -rotate-90">
              <circle
                cx="96"
                cy="96"
                r="88"
                stroke="rgba(255,255,255,0.1)"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="96"
                cy="96"
                r="88"
                stroke="url(#blood-gradient)"
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 88}`}
                strokeDashoffset={`${2 * Math.PI * 88 * (1 - getProgressPercentage() / 100)}`}
                strokeLinecap="round"
                className="transition-all duration-1000 ease-linear"
              />
              <defs>
                <linearGradient id="blood-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#b91c1c" />
                  <stop offset="100%" stopColor="#7f1d1d" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-5xl font-bold text-white" data-testid="exercise-timer-display">
                  {formatTime(timeLeft)}
                </div>
                <div className="text-stone-400 text-sm mt-2">
                  {!hasStarted ? 'Ready' : isRunning ? 'GO!' : timeLeft === 0 ? 'DONE!' : 'Paused'}
                </div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex gap-3 justify-center flex-wrap">
            {!hasStarted ? (
              <Button
                onClick={startTimer}
                className="bg-blood-700 hover:bg-blood-800 text-white font-bold px-8 py-3"
                data-testid="start-exercise-timer-btn"
              >
                <Play className="w-5 h-5 mr-2" />
                START
              </Button>
            ) : timeLeft > 0 ? (
              <>
                {isRunning ? (
                  <Button
                    onClick={pauseTimer}
                    variant="outline"
                    className="glass border-amber-500 text-amber-400 hover:bg-amber-500/20"
                    data-testid="pause-exercise-timer-btn"
                  >
                    <Pause className="w-4 h-4 mr-2" />
                    Pause
                  </Button>
                ) : (
                  <Button
                    onClick={resumeTimer}
                    className="bg-blood-700 hover:bg-blood-800"
                    data-testid="resume-exercise-timer-btn"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Resume
                  </Button>
                )}
                <Button
                  onClick={resetTimer}
                  variant="outline"
                  className="glass border-stone-500 text-stone-300 hover:bg-stone-500/20"
                  data-testid="reset-exercise-timer-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              </>
            ) : (
              <Button
                onClick={onClose}
                className="bg-blood-700 hover:bg-blood-800 text-white font-bold px-8"
                data-testid="done-exercise-timer-btn"
              >
                Done ✓
              </Button>
            )}
          </div>

          {/* Test Sound Button */}
          <div className="pt-2">
            <Button
              onClick={testChime}
              variant="ghost"
              size="sm"
              className="text-stone-500 hover:text-stone-300 text-xs"
            >
              <Volume2 className="w-3 h-3 mr-1" />
              Test Sound
            </Button>
          </div>

          {timeLeft === 0 && (
            <div className="text-center pt-2">
              <p className="text-blood-400 font-semibold text-lg">Exercise complete! Great work! 💪</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ExerciseTimer;
